// DashboardSpecForm
// Renders the full "Dashboard Spec" form including:
//   - Dashboard name
//   - Description sections (purpose, cannot answer, filters, what it answers, business rules)
//   - Data model (fact + bridge rows with join config)
//   - Temporal scope (with derive + load column buttons)
//   - Columns derived (per-view source table + column selectors)
//   - Questions (info-only note)
//   - Authorized users
//   - Source link
// All state and handlers are owned by ConsoleDashboard.tsx and passed in as props.

import React, { FormEvent, useState } from 'react';
import {
  ConsoleDashboardDescriptionSections,
  ConsoleDashboardDetail,
  ConsoleManagedTable,
  ColumnsDerivedGenerationResult,
} from '../../../types';
import { ModelRowState, ColumnsDerivedEntry } from './types';
import './DashboardSpecForm.css';
import dashboardInstructionsImg from '../../../Assets/dashboard_instructions.png';
interface TemporalScope {
  start_year?: number | null;
  end_year?: number | null;
  is_rolling: boolean;
  current_year_only: boolean;
}

interface DashboardSpecFormProps {
  loadingDetail: boolean;
  savingSpec: boolean;
  savingDescription: boolean;
  dashboardDetail: ConsoleDashboardDetail | null;
  name: string;
  setName: (v: string) => void;
  descriptionSections: ConsoleDashboardDescriptionSections;
  descriptionWords: number;
  filtersApplicableInput: string;
  setFiltersApplicableInput: (v: string) => void;
  updateSection: (key: keyof ConsoleDashboardDescriptionSections, value: string | string[]) => void;
  parseCsv: (v: string) => string[];
  handleSaveDescriptionSections: () => Promise<{ valid: boolean; words: number; emptyFields: string[] } | undefined>;
  modelRows: ModelRowState[];
  availableTables: ConsoleManagedTable[];
  tableById: Record<number, ConsoleManagedTable>;
  addBridgeRow: () => void;
  removeBridgeRow: (uid: string) => void;
  updateModelRow: (uid: string, patch: Partial<any>) => void;
  savingModelRows: Record<string, boolean>;
  deletingModelRows: Record<string, boolean>;
  handleDeleteModelRow: (uid: string, index: number, viewName: string, role: string) => Promise<void>;
  handleSaveModelRow: (uid: string, index: number) => Promise<{
    validationErrors?: string[];
    apiResult?: { is_validated: boolean; execution_error?: string | null };
  } | null>;
  temporalScope: TemporalScope;
  setTemporalScope: React.Dispatch<React.SetStateAction<TemporalScope>>;
  mainYearColumn: string;
  setMainYearColumn: (v: string) => void;
  mainYearColumnOptions: string[];
  loadingMainColumns: boolean;
  derivingTemporalScope: boolean;
  loadMainViewColumns: () => Promise<void>;
  deriveTemporalScope: () => Promise<void>;
  columnsDerivedByUid: Record<string, ColumnsDerivedEntry[]>;
  columnsDerivedResults: Record<string, ColumnsDerivedGenerationResult>;
  generatingColumnsByUid: Record<string, boolean>;
  handleGenerateColumnsDerived: (uid: string, viewName: string) => Promise<void>;
  handleSaveManualConstructionDescription: (viewName: string, viewColumn: string, description: string) => Promise<boolean>;
  tableColumnsById: Record<number, string[]>;
  resolveTableIdFromReference: (row: ModelRowState, tableRef: string) => number | null;
  addDerivedEntry: (uid: string) => void;
  updateDerivedEntry: (uid: string, index: number, patch: Partial<ColumnsDerivedEntry>) => void;
  removeDerivedEntry: (uid: string, index: number) => void;
  authorizedUsersInput: string;
  setAuthorizedUsersInput: (v: string) => void;
  savingAuthorizedUsers: boolean;
  handleSaveAuthorizedUsers: () => Promise<void>;
  userRole: string;
  sourceLink: string;
  setSourceLink: (v: string) => void;
  handleSaveSpec: (event: FormEvent) => Promise<void>;
  indexingDashboard: boolean;
  handleIndexNow: () => Promise<void>;
  syncingAccess: boolean;
  handleAccessSync: () => Promise<void>;
}

const DashboardSpecForm: React.FC<DashboardSpecFormProps> = ({
  loadingDetail,
  savingSpec,
  savingDescription,
  dashboardDetail,
  name,
  setName,
  descriptionSections,
  descriptionWords,
  filtersApplicableInput,
  setFiltersApplicableInput,
  updateSection,
  parseCsv,
  handleSaveDescriptionSections,
  modelRows,
  availableTables,
  tableById,
  addBridgeRow,
  removeBridgeRow,
  updateModelRow,
  savingModelRows,
  deletingModelRows,
  handleDeleteModelRow,
  handleSaveModelRow,
  temporalScope,
  setTemporalScope,
  mainYearColumn,
  setMainYearColumn,
  mainYearColumnOptions,
  loadingMainColumns,
  derivingTemporalScope,
  loadMainViewColumns,
  deriveTemporalScope,
  columnsDerivedByUid,
  columnsDerivedResults,
  generatingColumnsByUid,
  handleGenerateColumnsDerived,
  handleSaveManualConstructionDescription,
  tableColumnsById,
  resolveTableIdFromReference,
  addDerivedEntry,
  updateDerivedEntry,
  removeDerivedEntry,
  authorizedUsersInput,
  setAuthorizedUsersInput,
  savingAuthorizedUsers,
  handleSaveAuthorizedUsers,
  userRole,
  sourceLink,
  setSourceLink,
  handleSaveSpec,
  indexingDashboard,
  handleIndexNow,
  syncingAccess,
  handleAccessSync
}) => {
  const [descriptionWordError, setDescriptionWordError] = useState<string | null>(null);
  const [descriptionEmptyFields, setDescriptionEmptyFields] = useState<string[]>([]);
  const [modelRowErrors, setModelRowErrors] = useState<Record<string, string[]>>({});
  const [modelRowExecutionErrors, setModelRowExecutionErrors] = useState<Record<string, string>>({});


  const [showInstructions, setShowInstructions] = useState(false);

  const [emailRows, setEmailRows] = React.useState<string[]>(() =>
    authorizedUsersInput ? authorizedUsersInput.split('\n') : []
  );
  React.useEffect(() => {
    setEmailRows(authorizedUsersInput ? authorizedUsersInput.split('\n') : []);
  }, [authorizedUsersInput]);
  const updateEmailRows = (next: string[]) => {
    setEmailRows(next);
    setAuthorizedUsersInput(next.join('\n'));
  };

  // ── Manual Construction description panel state ──────────────────────────────
  const [mcDescPanels, setMcDescPanels] = React.useState<Record<string, { open: boolean; text: string; saving: boolean }>>({});
  const getMcKey = (uid: string, viewColumn: string) => `${uid}__${viewColumn}`;

  // Pre-populate description text from GET response (column_metadata.description)
  // so existing descriptions are visible when the dashboard loads.
  React.useEffect(() => {
    setMcDescPanels((prev) => {
      const next = { ...prev };
      modelRows.forEach((row) => {
        const result = columnsDerivedResults[row._uid];
        if (!result) return;
        (result.view.manual_construction || []).forEach((mc: any) => {
          const key = getMcKey(row._uid, mc.view_column);
          const existingDesc = mc.column_metadata?.description || '';
          // Only set if not already open/being edited
          if (!next[key]?.open) {
            next[key] = { open: false, text: existingDesc, saving: false };
          }
        });
      });
      return next;
    });
  }, [columnsDerivedResults, modelRows]);

  const openMcDescPanel = (uid: string, viewColumn: string) => {
    const key = getMcKey(uid, viewColumn);
    setMcDescPanels((prev) => ({ ...prev, [key]: { open: true, text: prev[key]?.text || '', saving: false } }));
  };
  const setMcDescText = (uid: string, viewColumn: string, text: string) => {
    const key = getMcKey(uid, viewColumn);
    setMcDescPanels((prev) => ({ ...prev, [key]: { ...prev[key], text } }));
  };
  const saveMcDesc = async (uid: string, viewName: string, viewColumn: string) => {
    const key = getMcKey(uid, viewColumn);
    const panel = mcDescPanels[key];
    if (!panel) return;
    setMcDescPanels((prev) => ({ ...prev, [key]: { ...prev[key], saving: true } }));
    const ok = await handleSaveManualConstructionDescription(viewName, viewColumn, panel.text);
    setMcDescPanels((prev) => ({ ...prev, [key]: { ...prev[key], saving: false, open: !ok } }));
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  // Returns 'dsf-textarea--error' when the field is in the empty list, else ''
  const textareaClass = (fieldName: string) =>
    descriptionEmptyFields.includes(fieldName) ? 'dsf-textarea--error' : '';

  const onSaveDescription = async () => {
    setDescriptionWordError(null);
    setDescriptionEmptyFields([]);
    const result = await handleSaveDescriptionSections();
    if (!result) return;
    if (result.emptyFields?.length) { setDescriptionEmptyFields(result.emptyFields); return; }
    if (!result.valid) {
      if (result.words < 200) setDescriptionWordError(`Too short — ${result.words} words. Minimum is 200.`);
      else if (result.words > 1000) setDescriptionWordError(`Too long — ${result.words} words. Maximum is 1000.`);
    }
  };

  const onSaveModelRow = async (uid: string, index: number, viewName: string) => {
    setModelRowErrors((prev) => { const next = { ...prev }; delete next[uid]; return next; });
    setModelRowExecutionErrors((prev) => { const next = { ...prev }; delete next[viewName]; return next; });
    const result = await handleSaveModelRow(uid, index);
    if (!result) return;
    if (result.validationErrors?.length) {
      setModelRowErrors((prev) => ({ ...prev, [uid]: result.validationErrors! }));
      return;
    }
    if (result.apiResult?.execution_error) {
      setModelRowExecutionErrors((prev) => ({ ...prev, [viewName]: result.apiResult!.execution_error! }));
    }
  };

  const ValidationBadge = ({ isValidated }: { isValidated?: boolean }) => (
    <div className={`dsf-validation-badge ${isValidated ? 'dsf-validation-badge--validated' : 'dsf-validation-badge--pending'}`}>
      <span>{isValidated ? '✓' : '⏳'}</span>
      <span>{isValidated ? 'Validated' : 'Validation Pending'}</span>
    </div>
  );

  return (
    <div className="console-card">
      <h2>Dashboard Spec</h2>
      {loadingDetail ? <p>Loading dashboard detail...</p> : null}
      {!dashboardDetail && !loadingDetail ? <p>Select a dashboard to edit.</p> : null}

      {dashboardDetail ? (
        <form className="console-form" onSubmit={handleSaveSpec}>

          {/* ── Dashboard Name ── */}
          <label>
            Dashboard Name (as Tableau)
            <input value={name} readOnly className="dsf-input-readonly" />
          </label>

          {/* ── Description Sections ── */}
          <div className="console-subcard">
            <h3>Description Sections</h3>
            <p>
              Required sections. Combined description must be 200-1000 words.{' '}
              Current:{' '}
              <strong className={descriptionWords < 200 || descriptionWords > 1000 ? 'dsf-word-count--invalid' : 'dsf-word-count--valid'}>
                {descriptionWords}
              </strong>
            </p>

            {descriptionEmptyFields.length ? (
              <p className="dsf-error-text">Required: {descriptionEmptyFields.join(', ')}</p>
            ) : null}
            {descriptionWordError ? (
              <p className="dsf-error-text">{descriptionWordError}</p>
            ) : null}

            <label>
              Dashboard Purpose
              <textarea
                rows={4}
                value={descriptionSections.dashboard_purpose}
                className={textareaClass('Dashboard Purpose')}
                onChange={(e) => { updateSection('dashboard_purpose', e.target.value); if (descriptionEmptyFields.length) setDescriptionEmptyFields([]); }}
              />
            </label>

            <label>
              What It Cannot Answer
              <textarea
                rows={4}
                value={descriptionSections.cannot_answer}
                className={textareaClass('What It Cannot Answer')}
                onChange={(e) => { updateSection('cannot_answer', e.target.value); if (descriptionEmptyFields.length) setDescriptionEmptyFields([]); }}
              />
            </label>

            <label>
              Filters That Can Be Applied
              <textarea
                rows={4}
                value={filtersApplicableInput}
                className={textareaClass('Filters That Can Be Applied')}
                onChange={(e) => {
                  setFiltersApplicableInput(e.target.value);
                  updateSection('filters_applicable', e.target.value);
                  if (descriptionEmptyFields.length) setDescriptionEmptyFields([]);
                }}
              />
            </label>

            <label>
              What It Answers
              <textarea
                rows={4}
                value={descriptionSections.what_it_answers}
                className={textareaClass('What It Answers')}
                onChange={(e) => { updateSection('what_it_answers', e.target.value); if (descriptionEmptyFields.length) setDescriptionEmptyFields([]); }}
              />
            </label>

            <label>
              Business Rules Logic
              <textarea
                rows={5}
                value={descriptionSections.business_rules_logic}
                className={textareaClass('Business Rules Logic')}
                onChange={(e) => { updateSection('business_rules_logic', e.target.value); if (descriptionEmptyFields.length) setDescriptionEmptyFields([]); }}
              />
            </label>

            <div className="dsf-badge-row">
              <ValidationBadge isValidated={dashboardDetail?.description_sections?.is_validated} />
            </div>

            <button type="button" onClick={onSaveDescription} disabled={savingDescription} className="dsf-save-desc-btn">
              {savingDescription ? 'Saving...' : 'Save Description Sections'}
            </button>
          </div>

          {/* ── Data Model ── */}
          <div className="console-subcard">
            <div className="inline-header">
              <h3>Data Model</h3>
              <button type="button" onClick={addBridgeRow}>Add Bridge Table</button>
            </div>

            {modelRows.map((row, index) => (
              <div key={row._uid} className="console-subcard">
                <div className="inline-header">
                  <h4>{index === 0 ? 'Main Table (Fact)' : `Bridge Table ${index}`}</h4>
                  {index > 0 ? (
                    <button
                      type="button"
                      disabled={!!deletingModelRows[row._uid]}
                      onClick={() => void handleDeleteModelRow(row._uid, index, row.view_name, 'bridge')}
                    >
                      {deletingModelRows[row._uid] ? 'Removing...' : 'Remove Bridge'}
                    </button>
                  ) : null}
                </div>

                {modelRowErrors[row._uid]?.length ? (
                  <div className="dsf-model-errors">
                    {modelRowErrors[row._uid].map((err) => (
                      <p key={err} className="dsf-error-text">{err}</p>
                    ))}
                  </div>
                ) : null}

                <label>Role (system-set)<input value={index === 0 ? 'fact' : 'bridge'} disabled /></label>
                <label>
                  View Name
                  <input
                    value={row.view_name}
                    onChange={(e) => updateModelRow(row._uid, { view_name: e.target.value })}
                    placeholder="core_metrics_fact_project_view"
                  />
                </label>
                <label>
                  Grain
                  <input value={row.grain} onChange={(e) => updateModelRow(row._uid, { grain: e.target.value })} placeholder="project" />
                </label>
                <label>
                  Alias
                  <input value={row.alias} onChange={(e) => updateModelRow(row._uid, { alias: e.target.value })} placeholder="p" />
                </label>
                <label>
                  Primary Key
                  <input
                    value={row.primary_key}
                    onChange={(e) => updateModelRow(row._uid, { primary_key: e.target.value })}
                    placeholder="id"
                  />
                </label>
                <label>
                  SQL View
                  <textarea
                    rows={8}
                    value={row.sql_view}
                    onChange={(e) => updateModelRow(row._uid, { sql_view: e.target.value })}
                    placeholder="SELECT ..."
                  />
                </label>
                {/* TODO: remove this check once backend enforces it */}
                {row.sql_view.includes('*') ? (
                  <p style={{ color: 'red', margin: '4px 0' }}>
                    Warning: SQL view contains <strong>*</strong>. Remove wildcard before saving.
                  </p>
                ) : null}

                <label>
                  Source Tables (hold Ctrl / Cmd to select multiple)
                  <select
                    multiple
                    value={(row.source_tables || []).map((st) => String(st.table_id))}
                    onChange={(e) => {
                      const selectedTables = Array.from(e.target.selectedOptions)
                        .map((option) => ({
                          table_id: Number(option.value),
                          table_name: tableById[Number(option.value)]?.postgres_table_name || '',
                        })).filter((st) => Number.isFinite(st.table_id));
                      updateModelRow(row._uid, { source_tables: selectedTables });
                    }}
                  >
                    {availableTables.map((table) => (
                      <option key={`model-${row._uid}-table-${table.table_id}`} value={String(table.table_id)}>
                        {table.postgres_table_name}
                      </option>
                    ))}
                  </select>
                </label>

                {index > 0 ? (
                  <div className="console-subcard">
                    <h4>Join to Main Fact</h4>
                    <label>
                      Fact Key
                      <input
                        value={row.join_to_fact?.fact_key || ''}
                        onChange={(e) =>
                          updateModelRow(row._uid, {
                            join_to_fact: {
                              fact_key: e.target.value,
                              this_key: row.join_to_fact?.this_key || '',
                              type: row.join_to_fact?.type ?? '',
                            },
                          })
                        }
                        placeholder="id"
                      />
                    </label>
                    <label>
                      Bridge Key
                      <input
                        value={row.join_to_fact?.this_key || ''}
                        onChange={(e) => updateModelRow(row._uid, { join_to_fact: { fact_key: row.join_to_fact?.fact_key || '', this_key: e.target.value, type: row.join_to_fact?.type ?? '' } })}
                        placeholder="project__c"
                      />
                    </label>
                    <label>
                      Join Type
                      <input
                        value={row.join_to_fact?.type ?? ''}
                        onChange={(e) => updateModelRow(row._uid, { join_to_fact: { fact_key: row.join_to_fact?.fact_key || '', this_key: row.join_to_fact?.this_key || '', type: e.target.value } })}
                        placeholder="exists"
                      />
                    </label>
                  </div>
                ) : null}

                <div className="dsf-model-save-row">
                  <button type="button" onClick={() => void onSaveModelRow(row._uid, index, row.view_name)} disabled={!!savingModelRows[row._uid] || row.sql_view.includes('*')}>
                    {savingModelRows[row._uid] ? 'Saving...' : `Save ${index === 0 ? 'Fact' : `Bridge ${index}`}`}
                  </button>
                  <ValidationBadge isValidated={row.is_validated} />
                </div>

                {modelRowExecutionErrors[row.view_name] ? (
                  <div className="dsf-execution-error">
                    <strong>Execution Error:</strong><br />{modelRowExecutionErrors[row.view_name]}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {/* ── Temporal Scope ── */}
          <div className="console-subcard">
            <h3>Temporal Scope</h3>
            <p>Load fact view columns first, then pick a year column and generate start/end year.</p>
            <div className="inline-header">
              <button type="button" onClick={loadMainViewColumns} disabled={loadingMainColumns}>
                {loadingMainColumns ? 'Loading Columns...' : 'Load Main View Columns'}
              </button>
              <button type="button" onClick={deriveTemporalScope} disabled={derivingTemporalScope || !mainYearColumn}>
                {derivingTemporalScope ? 'Generating...' : 'Generate Temporal Scope'}
              </button>
            </div>

            <label>
              Year Column
              <select value={mainYearColumn} onChange={(e) => setMainYearColumn(e.target.value)}>
                <option value="">Select year column</option>
                {mainYearColumnOptions.map((column) => (
                  <option key={`main-year-col-${column}`} value={column}>{column}</option>
                ))}
              </select>
            </label>
            <label>Start Year<input type="number" value={temporalScope.start_year ?? ''} readOnly className="dsf-input-readonly" /></label>
            <label>End Year<input type="number" value={temporalScope.end_year ?? ''} readOnly className="dsf-input-readonly" /></label>
            <label className="checkbox-row"><input type="checkbox" checked={temporalScope.is_rolling} disabled />Is Rolling</label>
            <label className="checkbox-row"><input type="checkbox" checked={temporalScope.current_year_only} disabled />Current Year Only</label>
            <div className="dsf-badge-row">
              <ValidationBadge isValidated={dashboardDetail?.temporal_scope?.is_validated} />
            </div>
          </div>

          {/* ── Columns Derived ── */}
          <div className="console-subcard">
            <h3>Columns Derived</h3>
            <p>Generate columns for each view. Each view is generated separately.</p>

            {modelRows.map((row, index) => {
              const result = columnsDerivedResults[row._uid];
              const isGenerating = !!generatingColumnsByUid[row._uid];
              const label = index === 0 ? 'Fact View' : `Bridge View ${index}`;

              return (
                <div key={`derived-${row._uid}`} className="console-subcard">
                  <div className="inline-header">
                    <h4>{label}: <code>{row.view_name || '(unnamed)'}</code></h4>
                    <button type="button" onClick={() => void handleGenerateColumnsDerived(row._uid, row.view_name)} disabled={isGenerating || !row.view_name}>
                      {isGenerating ? 'Generating...' : 'Generate Columns'}
                    </button>
                  </div>

                  {result ? (
                    <>
                      {(result.view.tables || []).map((table) => (
                        <div key={`tbl-${row._uid}-${table.table_name}`} className="dsf-table-section">
                          <p className="dsf-table-section-title">{table.table_name}</p>
                          <table className="dsf-table">
                            <thead>
                              <tr className="dsf-thead-grey">
                                <th className="dsf-th">View Column</th>
                                <th className="dsf-th">Source Column</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(table.columns || []).map((col, ci) => (
                                <tr key={`col-${row._uid}-${table.table_name}-${ci}`} className="dsf-tr">
                                  <td className="dsf-td">{col.view_column}</td>
                                  <td className="dsf-td">{col.source_column}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}

                      {(result.view.manual_construction || []).length > 0 ? (
                        <div className="console-subcard dsf-mc-subcard">
                          <h5 className="dsf-mc-title">Manual Construction</h5>
                          <table className="dsf-table">
                            <thead>
                              <tr className="dsf-thead-yellow">
                                <th className="dsf-th">View Column</th>
                                <th className="dsf-th">Source Table</th>
                                <th className="dsf-th">Source Column</th>
                                <th className="dsf-th"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.view.manual_construction.map((mc, mi) =>
                                (mc.sources || []).map((src, si) => (
                                  <React.Fragment key={`mc-${row._uid}-${mi}-${si}`}>
                                    <tr className="dsf-tr">
                                      {si === 0 ? <td className="dsf-td" rowSpan={mc.sources.length}>{mc.view_column}</td> : null}
                                      <td className="dsf-td">{src.source_table}</td>
                                      <td className="dsf-td">{src.source_column}</td>
                                      {si === 0 ? (
                                        <td className="dsf-td" rowSpan={mc.sources.length}>
                                          <button type="button" className="dsf-mc-add-desc-btn" onClick={() => openMcDescPanel(row._uid, mc.view_column)}>
                                            {mcDescPanels[getMcKey(row._uid, mc.view_column)]?.text ? 'Edit Description' : 'Add Description'}
                                          </button>
                                          {mcDescPanels[getMcKey(row._uid, mc.view_column)]?.text && !mcDescPanels[getMcKey(row._uid, mc.view_column)]?.open ? (
                                            <p className="dsf-mc-existing-desc">{mcDescPanels[getMcKey(row._uid, mc.view_column)].text}</p>
                                          ) : null}
                                        </td>
                                      ) : null}
                                    </tr>

                                    {si === 0 && mcDescPanels[getMcKey(row._uid, mc.view_column)]?.open ? (
                                      <tr>
                                        <td colSpan={4} className="dsf-mc-desc-cell">
                                          <div className="dsf-mc-desc-panel">
                                            <textarea
                                              rows={3}
                                              className="dsf-mc-desc-textarea"
                                              placeholder={`Description for ${mc.view_column}...`}
                                              value={mcDescPanels[getMcKey(row._uid, mc.view_column)]?.text || ''}
                                              onChange={(e) => setMcDescText(row._uid, mc.view_column, e.target.value)}
                                            />
                                            <div className="dsf-mc-desc-actions">
                                              <button
                                                type="button"
                                                disabled={mcDescPanels[getMcKey(row._uid, mc.view_column)]?.saving}
                                                onClick={() => void saveMcDesc(row._uid, row.view_name, mc.view_column)}
                                              >
                                                {mcDescPanels[getMcKey(row._uid, mc.view_column)]?.saving ? 'Saving...' : 'Save'}
                                              </button>
                                              <button
                                                type="button"
                                                className="dsf-mc-cancel-btn"
                                                onClick={() => setMcDescPanels((prev) => ({
                                                  ...prev,
                                                  [getMcKey(row._uid, mc.view_column)]: { ...prev[getMcKey(row._uid, mc.view_column)], open: false },
                                                }))}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : null}
                                  </React.Fragment>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <p className="dsf-no-columns-hint">No columns generated yet — click Generate Columns.</p>
                  )}
                </div>
              );
            })}

            <div className="dsf-badge-row">
              <ValidationBadge isValidated={dashboardDetail?.columns_derived?.is_validated} />
            </div>
          </div>

          {/* ── Questions (info only) ── */}
          <div className="console-subcard">
            <h3>Questions</h3>
            <p>Questions + SQL are no longer authored here. Use the Training tab only.</p>
          </div>

          {/* ── Authorized Users ── */}
          <div className="console-subcard">
            <div className="inline-header">
              <h3>Authorized Users</h3>
              {userRole === 'tableau_admin' ? (
                <button type="button" className="dsf-add-user-btn" title="Add user" onClick={() => updateEmailRows([...emailRows, ''])}>
                  +
                </button>
              ) : null}
            </div>

            {userRole !== 'tableau_admin' ? (
              <p className="dsf-auth-role-note">Only Tableau Admins can manage authorized users.</p>
            ) : null}

            {emailRows.length === 0 && userRole === 'tableau_admin' ? (
              <p className="dsf-auth-hint">No users added yet — click + to add one.</p>
            ) : null}

            {emailRows.map((email, i) => (
              <div key={`auth-user-${i}`} className="dsf-auth-user-row">
                <input
                  type="email"
                  value={email}
                  readOnly={userRole !== 'tableau_admin'}
                  className={`dsf-auth-user-input${userRole !== 'tableau_admin' ? ' dsf-auth-user-input--readonly' : ''}`}
                  placeholder="user@example.com"
                  onChange={(e) => {
                    if (userRole !== 'tableau_admin') return;
                    const next = [...emailRows];
                    next[i] = e.target.value;
                    updateEmailRows(next);
                  }}
                />
                {userRole === 'tableau_admin' ? (
                  <button type="button" className="dsf-auth-remove-btn" title="Remove" onClick={() => updateEmailRows(emailRows.filter((_, idx) => idx !== i))}>
                    ×
                  </button>
                ) : null}
              </div>
            ))}

            {userRole === 'tableau_admin' ? (
              <button type="button" className="dsf-save-auth-btn" onClick={handleSaveAuthorizedUsers} disabled={savingAuthorizedUsers}>
                {savingAuthorizedUsers ? 'Saving...' : 'Save Authorized Users'}
              </button>

            ) : null}
            {userRole === 'tableau_admin' ? (
              <button
                type="button"
                onClick={handleAccessSync}
                disabled={syncingAccess}
              >
                {syncingAccess ? 'Syncing...' : 'Access Sync'}
              </button>

            ) : null}

          </div>

          {/* ── Source Link ── */}
          <label>
            Source Link (Tableau URL)
            <input
              value={sourceLink}
              readOnly className="dsf-input-readonly"
            />
          </label>
          {/* ── Index Now ── */}
          <button
            type="button"
            onClick={handleIndexNow}
            disabled={indexingDashboard}
          >
            {indexingDashboard ? 'Indexing...' : 'Index Now'}
          </button>

          {/* <button type="submit" disabled={savingSpec}>
            {savingSpec ? 'Saving...' : 'Save Dashboard Spec'}
          </button> */}
        </form>
      ) : null}
      {/* ── Floating instructions button ── */}
<button
  type="button"
  className="dsf-instructions-btn"
  onClick={() => setShowInstructions(true)}
  title="View instructions"
>
  ⓘ
</button>

{showInstructions ? (
  <div className="dsf-instructions-overlay" onClick={() => setShowInstructions(false)}>
    <div className="dsf-instructions-modal" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="dsf-instructions-close"
        onClick={() => setShowInstructions(false)}
      >
        ×
      </button>
      <img
        src={dashboardInstructionsImg}
        alt="Dashboard Instructions"
        className="dsf-instructions-img"
      />
    </div>
  </div>
) : null}
    </div>
  );
};

export default DashboardSpecForm;